import {Component} from 'react';
import {I18n, Translate} from 'react-redux-i18n';
import {isEmpty} from 'front-onceforall-core/dist/utils/arrays';
import PropTypes from 'prop-types';
import {bindActionCreators} from 'redux';
import {Icon} from 'front-onceforall-core';
import {emailValidator} from 'front-onceforall-core/dist/utils/validators';
import TextField from '@material-ui/core/TextField';
import {connect} from 'react-redux';
import UserInvitation from './userInvitation';
import userService from '../../services/user/UserService';
import {nestedAccess} from '../../utils/objects';
import InviteUser from './inviteUser';
import {translateToLocale} from '../../translations';
import {errorMessage, errorType, INVITATION_TYPE} from '../../utils/constant';
import * as snackbarsActions from '../../actions/snackbars';
import authenticationService from '../../services/authentication/AuthenticationService';

const REQUIRED_FIELDS = [
  'email'
];

export class UserAddView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      confirmLoading: false,
      inviteLoading: false,
      openExternalInvitation: false,
      fieldErrors: {},
      showFieldErrors: false,
      email: this.props.email,
      invitations: [] // data structure: [{type: 'INTERNAL|EXTERNAL', user: {...}}]
    };

    this.handleDelete = this.handleDelete.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleAddUser = this.handleAddUser.bind(this);
    this.handleConfirmAddingUsers = this.handleConfirmAddingUsers.bind(this);
    this.translateInvitationMessage = this.translateInvitationMessage.bind(this);
    this.handleCancelExternalInvite = this.handleCancelExternalInvite.bind(this);
    this.handleValidateExternalInvite = this.handleValidateExternalInvite.bind(this);

    this.isBackOffice = authenticationService.roles().isBackOffice();
  }

  handleDelete(index) {
    return () => {
      const {invitations} = this.state;
      invitations.splice(index, 1);
      this.setState({
        invitations
      });
    };
  }

  handleAddUser() {
    this.checkIfSubmittable();
    if (!this.state.isSubmittable) {
      this.setState({showFieldErrors: true});
      return;
    }

    const {fieldErrors} = this.state;

    const userAlreadyInvited = this.props.users.find(user => user.email === this.state.email && !user.invited);

    if (userAlreadyInvited) {
      fieldErrors.email = <Translate value="user.invite.alreadyPartOfOrganisation"/>;
      this.setState({showFieldErrors: true, fieldErrors});
      return;
    }

    if (this.state.inviteLoading) {
      return;
    }

    this.setState({
      inviteLoading: true
    });
    userService.searchByEmail(this.state.email)
      .then(response => {
        const {invitations} = this.state;
        if (response.status === 204) {
          this.setState({
            openExternalInvitation: true,
            inviteLoading: false
          });
        } else {
          response.json().then(user => {
            invitations.push({
              type: INVITATION_TYPE.INTERNAL,
              user
            });
            this.setState({
              invitations,
              email: '',
              inviteLoading: false
            });
          });
        }
      });
  }

  handleInternalInvitations(internalUsers) {
    if (internalUsers.length <= 0) {
      return Promise.resolve();
    }

    return userService.linkOrganisationToUsers(this.props.organisation.organisationId, internalUsers);
  }

  handleExternalInvitations(externalUsers) {
    if (externalUsers.length <= 0) {
      return Promise.resolve();
    }

    return userService.linkOrganisationToNewUsers(this.props.organisation.organisationId, externalUsers);
  }

  handleConfirmAddingUsers() {
    if (this.state.confirmLoading) {
      return;
    }

    const {invitations} = this.state;
    const internalUsers = invitations.filter(invitation => invitation.type === INVITATION_TYPE.INTERNAL).map(
      invitation => invitation.user
    );
    const externalUsers = invitations.filter(invitation => invitation.type === INVITATION_TYPE.EXTERNAL)
      .map(invitation => ({...invitation.user, organisationName: this.props.organisation.fullName}));

    this.setState({
      confirmLoading: true
    });
    Promise.all([
      this.handleInternalInvitations(internalUsers),
      this.handleExternalInvitations(externalUsers)
    ])
      .then(() => {
        this.setState({
          invitations: [],
          confirmLoading: false
        });
        this.props.actions.addSuccess(<Translate
          value="user.invite.successMessage"
          count={internalUsers.length + externalUsers.length}
          name={internalUsers[0] ? internalUsers[0].fullName : externalUsers[0].email}
        />);
        this.props.onClose();
      })
      .catch(() => {
        this.setState({
          confirmLoading: false
        });
        this.props.actions.addError(<Translate value="error.generic"/>);
      });
  }

  checkIfSubmittable() {
    this.setState({
      isSubmittable: !this.validateFields()
    });
  }

  validateFields() {
    const internalInvitations = this.state.invitations.filter(
      invitation => invitation.type === INVITATION_TYPE.INTERNAL
    );

    const fieldErrors = {};
    let hasError = false;
    REQUIRED_FIELDS.forEach(fieldName => {
      const value = nestedAccess(fieldName, this.state);
      if (isEmpty(value) || value === false) {
        fieldErrors[fieldName] = <Translate value="error.required"/>;
        hasError = true;
      } else if (emailValidator(value) === 'error.invalidEmailFormat') {
        fieldErrors[fieldName] = <Translate value="error.validation"/>;
        hasError = true;
      } else if (internalInvitations.find(invitation => invitation.user.email === value)) {
        fieldErrors[fieldName] = <Translate value="user.invite.alreadySelected"/>;
        hasError = true;
      } else if (this.props.user.email === this.state.email) {
        fieldErrors[fieldName] = <Translate value="user.invite.self"/>;
        hasError = true;
      } else {
        fieldErrors[fieldName] = null;
      }
    });
    this.setState({
      fieldErrors
    });
    return hasError;
  }

  handleInputChange(event) {
    const {value} = event.target;
    this.setState({
      email: value
    }, () => this.checkIfSubmittable());
  }

  translateInvitationMessage(language = this.props.currentLocale) {
    return translateToLocale(language, 'user.invite.defaultMessage', {
      organisationName: this.props.organisation.fullName,
      userName: this.props.user.fullName
    });
  }

  handleCancelExternalInvite() {
    this.setState({
      openExternalInvitation: false,
      email: ''
    });
  }

  handleValidateExternalInvite(externalInvitation) {
    const invitationAlreadySent = this.state.invitations.find(invitation => invitation.user.email === externalInvitation.email);
    if (invitationAlreadySent) {
      // @TODO new Error()
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        type: errorType.INVITATION_ALREADY_SENT,
        message: errorMessage.USER_ALREADY_INVITED
      });
    }

    const userAlreadyInOrganisation = this.props.users.find(user => user.email === externalInvitation.email);
    if (userAlreadyInOrganisation) {
      // @TODO new Error()
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        type: errorType.USER_ALREADY_IN_ORGANISATION,
        message: errorMessage.USER_ALREADY_IN_ORGANISATION
      });
    }

    const invitations = [
      ...this.state.invitations,
      {
        type: INVITATION_TYPE.EXTERNAL,
        user: externalInvitation
      }
    ];
    this.setState({
      invitations,
      openExternalInvitation: false,
      email: ''
    });
    return Promise.resolve();
  }

  getFieldErrorText(fieldName) {
    if (this.state.fieldErrors === null || typeof (this.state.fieldErrors[fieldName]) === 'undefined' || !this.state.showFieldErrors) {
      return null;
    }

    return this.state.fieldErrors[fieldName];
  }

  render() {
    const usersInvitations = this.state.invitations.map((invitation, index) => <UserInvitation
      invitation={invitation}
      key={index}
      onDelete={this.handleDelete(index)}
    />);

    const tooltipTitle = this.state.invitations.length === 0 ? 'user.notSelectedWarning' : 'user.maxUsersReached';
    const canAddUser = this.isBackOffice || this.state.invitations.length <= this.props.remainingUserAccounts;
    const onlyBO = this.isBackOffice && this.state.invitations.length > this.props.remainingUserAccounts;
    const confirmButton = (this.state.invitations.length > 0) && canAddUser ?
      (
        <button
          type="button"
          className={`large inline-container no-wrap ${onlyBO ? 'bo' : 'primary'}`}
          data-id="addUserButton"
          onClick={this.handleConfirmAddingUsers}
          disabled={this.state.confirmLoading}
        >
          <Icon icon={this.state.confirmLoading ? 'loader' : 'check'}/>
          <Translate value="action.confirm"/>
        </button>
      ) : (
        <span
          tooltip={I18n.t(tooltipTitle)}
          flow="left"
        >
          <button
            type="button"
            className="large primary inline-container no-wrap"
            data-id="addUserButton"
            onClick={null}
            disabled
          >
            <Icon icon="check"/>
            <Translate value="action.confirm"/>
          </button>
        </span>
      );

    const inviteButton = canAddUser ?
      (
        <button
          data-id="inviteUserButton"
          type="button"
          className={`large inline-container no-wrap ${onlyBO ? 'bo' : 'primary'}`}
          onClick={this.handleAddUser}
          disabled={this.state.inviteLoading}
        >
          <Translate value="action.invite"/>
        </button>
      ) : (
        <span
          tooltip={I18n.t('user.maxUsersReached')}
          flow="left"
        >
          <button
            data-id="inviteUserButton"
            type="button"
            className="large primary inline-container no-wrap"
            onClick={null}
            disabled
          >
            <Translate value="action.invite"/>
          </button>
        </span>
      );

    return (
      <div>
        {this.state.openExternalInvitation ?
          <InviteUser
            actions={this.props.actions}
            onCancel={this.handleCancelExternalInvite}
            onValidate={this.handleValidateExternalInvite}
            translateInvitationMessage={this.translateInvitationMessage}
            currentLocale={this.props.currentLocale}
            userMail={this.state.email}
            panelTitle="user.invite.title"
          /> :
          <div>
            <h2 className="col-12 mb-4"><Translate value="user.title"/></h2>
            <form>
              <table>
                <tbody>
                  {usersInvitations}
                </tbody>
              </table>
              <div className="col-12 mt-4">
                <TextField
                  autoFocus
                  label={<span><Translate value="user.email"/> *</span>}
                  error={Boolean(this.getFieldErrorText('email'))}
                  helperText={this.getFieldErrorText('email')}
                  data-id="email"
                  value={this.state.email}
                  onChange={this.handleInputChange}
                  InputProps={{
                    endAdornment: (
                      inviteButton
                    )
                  }}
                />
              </div>
              <div className="actions col-12 inline-container space-between">
                <button
                  type="button"
                  className="large secondary inline-container"
                  onClick={this.props.onClose}
                >
                  <Icon icon="go-back"/>
                  <Translate value="action.cancel"/>
                </button>
                {confirmButton}
              </div>
            </form>
          </div>
        }
      </div>
    );
  }
}

UserAddView.propTypes = {
  actions: PropTypes.object.isRequired,
  organisation: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  users: PropTypes.array.isRequired,
  currentLocale: PropTypes.string.isRequired,
  remainingUserAccounts: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  email: PropTypes.string
};

UserAddView.defaultProps = {
  email: ''
};

function mapStateToProps(state) {
  return {
    organisation: state.organisation,
    user: state.user,
    currentLocale: state.i18n.locale
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({...snackbarsActions}, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(UserAddView);
